"use client";

import React from "react";
import { 
  X, 
  Utensils, 
  Wine, 
  Calendar, 
  Clock, 
  Users, 
  Mail, 
  Phone, 
  ShieldCheck, 
  Trash2,
  Sparkles,
  Award
} from "lucide-react";
import { RestaurantReservation } from "@/lib/demos/restaurantDb";

interface RestaurantDetailModalProps {
  reservation: RestaurantReservation | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete?: (id: number) => void;
}

export default function RestaurantDetailModal({
  reservation,
  isOpen,
  onClose,
  onDelete,
}: RestaurantDetailModalProps) {
  if (!isOpen || !reservation) return null;

  const sym = reservation.currency === "USD" ? "$" : "₹";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-xl bg-stone-950 border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 bg-stone-900/60">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Utensils className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white tracking-tight">{reservation.guest_name}</h3>
              <p className="text-xs text-stone-400">Dossier #{reservation.id} • {reservation.occasion}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Status Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-stone-900/80 rounded-xl border border-stone-800">
            <div>
              <span className="text-[11px] text-stone-400 uppercase tracking-wider block">Current Stage</span>
              <span className="text-xs font-bold text-amber-400">{reservation.stage}</span>
            </div>

            <div>
              <span className="text-[11px] text-stone-400 uppercase tracking-wider block">Assigned Table</span>
              <span className="text-xs font-bold text-white">{reservation.table_number || "Not Yet Assigned"}</span>
            </div>

            <div>
              <span className="text-[11px] text-stone-400 uppercase tracking-wider block">Est. Revenue</span>
              <span className="text-xs font-bold text-emerald-400">{sym}{reservation.estimated_spend.toLocaleString()}</span>
            </div>
          </div>

          {/* Contact Details */}
          <div className="space-y-2">
            <span className="text-xs font-bold text-amber-400 uppercase tracking-wider block">
              Diner Contact Details
            </span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs bg-stone-900/40 p-4 rounded-xl border border-stone-800/80">
              <div className="flex items-center gap-2 text-stone-300">
                <Mail className="w-4 h-4 text-stone-500 shrink-0" />
                <span className="truncate">{reservation.email}</span>
              </div>
              <div className="flex items-center gap-2 text-stone-300">
                <Phone className="w-4 h-4 text-stone-500 shrink-0" />
                <span>{reservation.phone || "No phone provided"}</span>
              </div>
            </div>
          </div>

          {/* Reservation Particulars */}
          <div className="space-y-2">
            <span className="text-xs font-bold text-amber-400 uppercase tracking-wider block">
              Dining Itinerary & Allocation
            </span>
            <div className="grid grid-cols-2 gap-3 text-xs bg-stone-900/40 p-4 rounded-xl border border-stone-800/80">
              <div>
                <span className="text-stone-400 block">Date & Sitting</span>
                <span className="text-white font-semibold">{reservation.reservation_date} • {reservation.time_slot}</span>
              </div>
              <div>
                <span className="text-stone-400 block">Party Size</span>
                <span className="text-white font-semibold">{reservation.party_size} Guests</span>
              </div>
              <div>
                <span className="text-stone-400 block">Dining Salon</span>
                <span className="text-white font-semibold">{reservation.dining_area}</span>
              </div>
              <div>
                <span className="text-stone-400 block">Lead Sommelier</span>
                <span className="text-amber-300 font-semibold">{reservation.assigned_sommelier}</span>
              </div>
            </div>
          </div>

          {/* Tasting Menu & Notes */}
          <div className="space-y-3">
            <div className="p-3.5 bg-stone-900/60 rounded-xl border border-stone-800 space-y-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400 block">
                Selected Tasting Menu
              </span>
              <p className="text-xs text-white font-medium">{reservation.tasting_menu}</p>
            </div>

            {reservation.dietary_notes && (
              <div className="p-3.5 bg-amber-500/5 rounded-xl border border-amber-500/20 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-400 block">
                  Dietary Restrictions & Allergies
                </span>
                <p className="text-xs text-stone-300">{reservation.dietary_notes}</p>
              </div>
            )}

            {reservation.special_requests && (
              <div className="p-3.5 bg-stone-900/60 rounded-xl border border-stone-800 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-stone-400 block">
                  Special Table Requests / Notes
                </span>
                <p className="text-xs text-stone-300">{reservation.special_requests}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-stone-800 bg-stone-900/40 flex items-center justify-between">
          {onDelete && (
            <button
              onClick={() => {
                if (confirm("Are you sure you want to delete this reservation?")) {
                  onDelete(reservation.id);
                  onClose();
                }
              }}
              className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Delete Reservation</span>
            </button>
          )}

          <button
            onClick={onClose}
            className="ml-auto px-4 py-2 rounded-xl bg-stone-800 hover:bg-stone-700 text-stone-200 text-xs font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
